import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import type { OrderDTO, OrderItemDTO } from '@sac/contracts'
import { ChannelMark, Icon, channelName } from '../icons'
import { Menu } from '../ui/Menu'
import { useToast } from '../ui/Toaster'

function money(n: number) {
  return `R$ ${n.toFixed(2).replace('.', ',')}`
}

/**
 * Product thumbnail.
 *
 * The marketplace image is hotlinked from its own CDN rather than mirrored: it is already served
 * fast and globally, and copying it would mean owning its storage and expiry for no gain.
 *
 * `no-referrer` because some marketplace CDNs refuse requests carrying a foreign referrer, and
 * `onError` falls back to the placeholder — a dead image icon in the middle of an order card
 * reads as "the order is broken", which is a worse lie than a plain box.
 */
function ProductThumb({ item }: { item: OrderItemDTO }) {
  const [failed, setFailed] = useState(false)
  if (item.imageUrl && !failed) {
    return (
      <img
        className="pthumb-img"
        src={item.imageUrl}
        alt={item.productName}
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
      />
    )
  }
  return <span className="pthumb">{item.imageEmoji ?? '📦'}</span>
}

export function OrderCard({ order, primary }: { order: OrderDTO; primary: boolean }) {
  const [open, setOpen] = useState(false)
  const toast = useToast()

  return (
    <div className="ocard">
      {order.channelType !== 'site' && (
        <div className="mk-head">
          <ChannelMark channel={order.channelType} size={15} />
          {channelName(order.channelType)}
          {order.buyerLabel ? ` · Comprador ${order.buyerLabel}` : ''}
        </div>
      )}
      <div className="ocard-h">
        <span className="num">#{order.externalOrderId}</span>
        <span className={`pill ${order.statusTone}`}>{order.status}</span>
      </div>
      <div className="ocard-sub">
        {new Date(order.createdAt).toLocaleDateString('pt-BR')} · {money(order.total)}
      </div>
      {/* Every line, not just the first: a two-item order showing one item is how an agent
          confidently tells a customer the wrong thing. */}
      {order.items.map((item) => (
        <div className="ocard-p" key={item.id}>
          <ProductThumb item={item} />
          <span className="t">
            {item.productName}
            {item.quantity > 1 && <span className="qty">×{item.quantity}</span>}
            <br />
            {item.variationName && <span className="varia">{item.variationName}</span>}
            <span style={{ fontSize: 11, color: 'var(--text-3)', fontFamily: 'ui-monospace,Menlo,monospace' }}>
              {item.sku}
            </span>
          </span>
        </div>
      ))}
      {primary && order.trackingStatus && (
        <div className="track">
          <div className="r1">
            <Icon name="truck" size={14} />
            {order.carrier}
          </div>
          <div className="r2">{order.trackingStatus}</div>
          {order.estimatedDelivery && (
            <div className="r3">Previsão: {new Date(order.estimatedDelivery).toLocaleDateString('pt-BR')}</div>
          )}
        </div>
      )}
      <div className="ocard-f">
        <button
          className="btn sec sm"
          onClick={() => toast(`${order.carrier ?? ''} · ${order.trackingCode ?? '—'} — ${order.trackingStatus ?? ''}`)}
        >
          Rastrear
        </button>
        <button className="btn sec sm" onClick={() => setOpen((o) => !o)}>
          {open ? 'Ocultar' : 'Detalhes'}
        </button>
        <MoreMenu order={order} />
      </div>
      {open && (
        <div className="ocard-more on">
          <div className="dl">
            <span className="k">Código</span>
            <span className="v">{order.trackingCode ?? '—'}</span>
          </div>
          <div className="dl">
            <span className="k">Pagamento</span>
            <span className="v">{order.paymentMethod ?? '—'}</span>
          </div>
          <div className="dl">
            <span className="k">Endereço</span>
            <span className="v">{order.shippingAddress ?? '—'}</span>
          </div>
          {order.invoiceNumber && (
            <div className="dl">
              <span className="k">Nota fiscal</span>
              <span className="v">{order.invoiceNumber}</span>
            </div>
          )}
          {order.trackingHistory.length > 0 && (
            <>
              <div className="blk-h" style={{ margin: '11px 0 6px' }}>
                Rastreamento
              </div>
              {order.trackingHistory.map((h, i) => (
                <div
                  key={i}
                  style={{ fontSize: 12, color: 'var(--text-2)', padding: '3px 0 3px 12px', borderLeft: '2px solid var(--border)' }}
                >
                  {h}
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Order actions. Uses the shared Menu so the popup escapes the card's own `overflow:hidden`
 * — hand-rolled here, it was clipped to a sliver, and it closed on neither Esc nor an
 * outside click.
 */
function MoreMenu({ order }: { order: OrderDTO }) {
  const toast = useToast()
  const qc = useQueryClient()

  /**
   * Ask the channel again where this order is.
   *
   * Mercado Livre only: Shopee's status already IS the delivery state and the site carries its
   * own tracking. The suggestion path reads this on its own, but a channel that was down when it
   * ran leaves the card showing the old answer — and the person looking at the card is the one
   * who knows a customer is waiting.
   */
  const refresh = useMutation({
    mutationFn: () => api.refreshDelivery(order.id),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['conversations'] })
      qc.invalidateQueries({ queryKey: ['conversation'] })
      toast(
        r.refreshed
          ? `Entrega atualizada: ${r.trackingStatus ?? 'sem detalhe'}`
          : `Não foi possível consultar agora${r.error ? ` — ${r.error}` : ''}`,
      )
    },
    onError: () => toast('Não foi possível consultar a entrega.'),
  })

  return (
    <div style={{ marginLeft: 'auto' }}>
      <Menu
        align="right"
        trigger={(open) => (
          <button className="icon-btn" style={{ width: 28, height: 28 }} onClick={open}>
            <Icon name="dots" />
          </button>
        )}
      >
        {(close) => (
          <>
            {order.channelType === 'mercadolivre' && (
              <button
                className="pop-item"
                disabled={refresh.isPending}
                onClick={() => {
                  refresh.mutate()
                  close()
                }}
              >
                {refresh.isPending ? 'Consultando…' : 'Atualizar entrega'}
              </button>
            )}
            {['Troca / devolução', 'Reenviar', 'Cancelar', 'Reembolsar', 'Nota fiscal'].map((a) => (
              <button
                key={a}
                className="pop-item"
                onClick={() => {
                  toast(`Fluxo de "${a}" (não implementado no protótipo) · pedido #${order.externalOrderId}`)
                  close()
                }}
              >
                {a}
              </button>
            ))}
          </>
        )}
      </Menu>
    </div>
  )
}
